import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import { useTranslation } from "i18n-fe";
import { useScoreContext } from "../context/ScoreContext";
import { syncEngine } from "../local/sync";
import { rebuildDatabase } from "../local/db";
import { flushAndRebuildLocalData } from "../local/rebuild";

// #1902 — support-only recovery control, mounted beside the existing
// PinLock (packages/common-fe/src/components/PinLock.jsx, NOT modified by
// this card — see AppShell.jsx). Deliberately a sibling fixed-position
// button rather than anything inside PinLock itself: card #2112 is removing
// PinLock from announcer-fe and touching its mounting concurrently.
//
// Gate (AC1): renders only once `pickerUnlocked` is true — the SAME state
// PinLock's onUnlock already sets via unlockPicker() in ScoreContext. No
// second PIN prompt is added; a locked scribe never sees this button at
// all (it isn't just hidden, it isn't rendered).
export default function RebuildLocalDataButton() {
  // #1902 — the rebuild.* strings live in locales/en/score.json (the
  // "score" namespace, same as InitialSetup's own useTranslation("score")).
  // useTranslation() with no namespace argument falls back to the DEFAULT
  // namespace and silently fails to resolve these keys, rendering the raw
  // key names ("rebuild.dialogTitle" etc.) instead of the actual copy —
  // caught by a real-Chrome screenshot. The vitest/jsdom unit test could
  // not catch it: its i18n-fe mock resolves keys against score.json
  // directly and DISCARDS the namespace argument, so it stayed green
  // while every string on the confirmation dialog was broken.
  const { t } = useTranslation("score");
  const navigate = useNavigate();
  const { pickerUnlocked } = useScoreContext();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // #1902 AC3 — set only when a confirmed run came back with ops still
  // queued; the dialog switches to the "still has unsynced work" message
  // and nothing is deleted.
  const [blocked, setBlocked] = useState(false);

  if (!pickerUnlocked) return null;

  const handleOpen = () => {
    setBlocked(false);
    setOpen(true);
  };

  const handleClose = () => {
    if (busy) return;
    setOpen(false);
    setBlocked(false);
  };

  const handleConfirm = async () => {
    setBusy(true);
    try {
      const result = await flushAndRebuildLocalData({
        syncEngine,
        rebuildDb: rebuildDatabase,
      });
      if (result.ok) {
        setOpen(false);
        // #628-style pattern: land the scribe back on a usable screen, same
        // route the lock's own onUnlock sends them to, since a rebuild
        // sends them back through show selection anyway (card notes).
        navigate("/setup");
      } else {
        setBlocked(true);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        aria-label={t("rebuild.buttonAria")}
        data-testid="rebuild-local-data-button"
        sx={{
          position: "fixed",
          bottom: 12,
          // #628 — PinLock sits at right:12, width 40; this sits immediately
          // to its left with an 8px gap (12 + 40 + 8 = 60).
          right: 60,
          zIndex: 1300,
          color: "text.secondary",
          bgcolor: "background.paper",
          border: 1,
          borderColor: "divider",
          width: 40,
          height: 40,
          "&:hover": { bgcolor: "action.hover" },
        }}
      >
        <RestartAltIcon sx={{ fontSize: 20 }} />
      </IconButton>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        data-testid="rebuild-local-data-dialog"
      >
        {blocked ? (
          <>
            <DialogTitle>{t("rebuild.blockedTitle")}</DialogTitle>
            <DialogContent>
              <DialogContentText data-testid="rebuild-blocked-message">
                {t("rebuild.blockedBody")}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button color="inherit" onClick={handleClose} autoFocus>
                {t("rebuild.close")}
              </Button>
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>{t("rebuild.dialogTitle")}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t("rebuild.dialogBody")}</DialogContentText>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="error" fontWeight={600}>
                  {t("rebuild.dialogWarning")}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button color="inherit" onClick={handleClose} disabled={busy}>
                {t("rebuild.cancel")}
              </Button>
              <Button
                color="error"
                onClick={handleConfirm}
                disabled={busy}
                data-testid="rebuild-confirm-button"
              >
                {busy ? t("rebuild.working") : t("rebuild.confirm")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
